import type { PropsWithChildren } from 'react'
import type { Message, MessageInitShape } from '@bufbuild/protobuf'
import { create, toBinary, toJsonString } from '@bufbuild/protobuf'
import type { GenMessage } from '@bufbuild/protobuf/codegenv1'
import type { Agent } from '@fingerprintjs/fingerprintjs'
import { load } from '@fingerprintjs/fingerprintjs'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { getAuthStatus } from '@jotta/auth-client/useAuthStatus'
import { minimumRequiredPasswordLength } from '@jotta/config/GlobalConfig'
import { useCheckEmail } from '@jotta/grpc-connect-client/customer/useCheckEmail'
import { useDescribeVoucher } from '@jotta/grpc-connect-client/signup/useDescribeVoucher'
import { DescribeVoucherResponseSchema } from '@jotta/grpc-connect-openapi/signup'
import { useBrandCode } from '@jotta/ui/useBrandTheme'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

export const minimumPasswordLength = minimumRequiredPasswordLength

const agentPromise = load()
const algorithm = 'AES-GCM'

export async function generateSignupToken<T extends Message>(
  schema: GenMessage<T>,
  data: MessageInitShape<GenMessage<T>>,
  updateToken: (message: T, token: Uint8Array) => void,
  agent?: Agent,
): Promise<T> {
  const message = create(schema, data)
  const authStatus = getAuthStatus()

  const theAgent = agent || (await agentPromise)
  const fingerprintResult = await theAgent.get()
  const visitorId = fingerprintResult.visitorId

  const preliminaryToken = `${Date.now()}:${visitorId}:${authStatus?.serverTimeMillis}`
  updateToken(message, new TextEncoder().encode(preliminaryToken))
  const messageData = toBinary(schema, message)

  const key = await crypto.subtle.generateKey(
    {
      name: algorithm,
      length: 128,
    },
    true,
    ['encrypt'],
  )
  const exportedKey = await crypto.subtle.exportKey('raw', key)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const cipherText = await crypto.subtle.encrypt(
    { name: algorithm, iv: iv },
    key,
    messageData,
  )

  const byteLength = cipherText.byteLength
  const keyLength = exportedKey.byteLength
  const ivLength = iv.byteLength

  const uint8Array = new Uint8Array(keyLength + ivLength + byteLength)
  uint8Array.set(new Uint8Array(exportedKey))
  uint8Array.set(iv, keyLength)
  uint8Array.set(new Uint8Array(cipherText), keyLength + ivLength)

  updateToken(message, uint8Array)

  return message
}

export function useActivationCodeTerminology() {
  const brandCode = useBrandCode()
  switch (brandCode) {
    case 'MEDIAMARKT':
    case 'LETS_GO':
      return true
    case 'ONLIME_WL':
    case 'ELGIGANTEN':
    case 'PHONERO':
    case 'HALEBOP':
    case 'ELKO':
    case 'TELIA_CLOUD':
    case 'GIGANTTI':
    case 'OBOS':
    case 'SATURN':
    case 'TEST':
    case 'ELKJOP':
    case 'GET':
    case 'TDC_NO':
    case 'COMHEM':
    case 'JOTTACLOUD':
      return false
    default:
      return exhaustiveGuard(brandCode)
  }
}

export function EmailInUse({
  onRequestLogin,
}: {
  onRequestLogin?: () => void
}) {
  const LoginRedirect = ({ children }: PropsWithChildren) =>
    onRequestLogin ? (
      <button
        className="btn btn-link"
        onClick={event => {
          event.preventDefault()
          onRequestLogin()
        }}
      >
        {children}
      </button>
    ) : (
      <a className="link" href="/api/login">
        {children}
      </a>
    )

  return (
    <span>
      <Trans>
        Email already in use. Would you like to{' '}
        <LoginRedirect>log in instead?</LoginRedirect>
      </Trans>
    </span>
  )
}

export function useNameField() {
  return {
    type: 'text',
    required: t`We need your name in order to sign you up`,
  } as const
}

export function useEmailField(
  customerGroupCode: string,
  onRequestLogin?: () => void,
) {
  const { mutateAsync } = useCheckEmail()

  return {
    type: 'email',
    required: t`We need your email in order to sign you up`,
    validate: async (email?: string) => {
      if (!email) {
        return undefined
      }

      const checkEmailResponse = await mutateAsync({
        customerGroupCode,
        email,
      })

      const result = checkEmailResponse.result
      switch (result.case) {
        case undefined:
        case 'emailNotUsed':
          return undefined
        case 'emailUsed':
          return {
            message: t`Email address in use!`,
            view: <EmailInUse onRequestLogin={onRequestLogin} />,
          }
        case 'emailInvalid':
          return t`We were unable to recognize "${email}" as an email address. Please look for typos and try again`
        default:
          exhaustiveGuard(result)
      }
    },
  } as const
}

export function usePasswordField(options?: { focusOnMount: boolean }) {
  return {
    type: 'text',
    autoComplete: 'new-password',
    required: true,
    validate: (password?: string) =>
      !password || password.length < minimumPasswordLength
        ? t`Create a password with ${minimumPasswordLength} characters or more`
        : undefined,
    ...options,
  } as const
}

export function useAcceptedTermsField() {
  return {
    type: 'boolean',
    required: t`We need you to accept the terms and conditions in order to continue`,
  } as const
}

export function useVoucherCodeField(
  customerGroupCode: string,
  setCustomerGroupCode: (voucherCustomerGroupCode: string) => void,
) {
  const { mutateAsync: describeVoucher } = useDescribeVoucher()
  const activationCodeTerminology = useActivationCodeTerminology()
  return {
    type: 'text',
    validate: async (voucherCode: string) => {
      if (!voucherCode) {
        return
      }
      const describeVoucherResponse = await describeVoucher({
        voucherCode,
        customerGroupCode,
      })
      const status = describeVoucherResponse.status
      switch (status.case) {
        case undefined:
          throw new Error(
            toJsonString(
              DescribeVoucherResponseSchema,
              describeVoucherResponse,
            ),
          )
        case 'invalid':
          return activationCodeTerminology
            ? t`The activation code is invalid!`
            : t`The voucher code is invalid!`
        case 'used':
          return activationCodeTerminology
            ? t`Your activation code is already in use! Please verify that the code is correct`
            : t`Your voucher code is already in use! Please verify that the code is correct.`
        case 'valid':
          setCustomerGroupCode(status.value.customerGroupCode)
          return undefined
        default:
          exhaustiveGuard(status)
      }
    },
  } as const
}

export const customerGroupCodeField = {
  type: 'text',
  required: true,
} as const

export const newsletterField = {
  type: 'boolean',
} as const
